import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from '../utils/axiosConfig';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);
  const isRefreshingRef = useRef(false);

  // トークンリフレッシュ関数
  const refreshToken = useCallback(async () => {
    if (isRefreshingRef.current) {
      return;
    }

    try {
      isRefreshingRef.current = true;
      console.log('Periodic token refresh...');

      await axios.post(`${process.env.REACT_APP_API_URL}/auth/refresh`, {}, {
        withCredentials: true,
        _isRefreshRequest: true
      });

      console.log('Periodic token refresh successful');
    } catch (error) {
      console.error('Periodic token refresh failed:', error);
      // リフレッシュに失敗した場合は定期更新を停止
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } finally {
      isRefreshingRef.current = false;
    }
  }, []);

  // 定期的なトークンリフレッシュを開始（20分ごと）
  const startPeriodicRefresh = useCallback(() => {
    // 既存のインターバルをクリア
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // 20分ごとにリフレッシュ（アクセストークンの期限30分より余裕を持って）
    intervalRef.current = setInterval(() => {
      refreshToken();
    }, 20 * 60 * 1000); // 20分

    console.log('Periodic token refresh started (every 20 minutes)');
  }, [refreshToken]);

  // 定期的なトークンリフレッシュを停止
  const stopPeriodicRefresh = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      console.log('Periodic token refresh stopped');
    }
  }, []);

  // ユーザー情報を再取得する関数（ログイン後に呼び出す）
  const refreshUserInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/me`, {
        withCredentials: true,
      });
      setUser(response.data);

      // ユーザー認証成功時に定期リフレッシュを開始
      startPeriodicRefresh();

      return response.data;
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error('Failed to refresh user info:', error);
      }
      setUser(null);
      stopPeriodicRefresh();
      throw error;
    }
  }, [startPeriodicRefresh, stopPeriodicRefresh]);

  // 初回ユーザー情報取得
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/me`, {
          withCredentials: true,
        });
        setUser(response.data);

        // ユーザー認証成功時に定期リフレッシュを開始
        startPeriodicRefresh();
      } catch (error) {
        // 401エラーは正常な動作（未ログイン状態）なのでログ出力しない
        if (error.response?.status !== 401) {
          console.error('Failed to fetch user:', error);
        }
        setUser(null);
        stopPeriodicRefresh();
      } finally {
        setLoading(false);
      }
    };

    fetchUser();

    // コンポーネントのアンマウント時にインターバルをクリア
    return () => {
      stopPeriodicRefresh();
    };
  }, [startPeriodicRefresh, stopPeriodicRefresh]);

  // ページの可視性変更時の処理
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // ページが非表示になった時は定期リフレッシュを停止
        stopPeriodicRefresh();
      } else if (user) {
        // ページが再表示された時は定期リフレッシュを再開
        startPeriodicRefresh();
        // 即座にトークンをリフレッシュ
        refreshToken();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, refreshToken, startPeriodicRefresh, stopPeriodicRefresh]);

  const value = {
    user,
    setUser,
    loading,
    refreshToken,
    refreshUserInfo,
    startPeriodicRefresh,
    stopPeriodicRefresh
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// カスタムフックとしてエクスポート
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
